import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { API_ENDPOINTS } from '../../config/api';
import {
  DEFAULT_EMPLOYEE_PERMISSIONS,
  normalizeEmployeePermissions,
} from '../../utils/companyEmployeePermissions';
import '../../config/axiosConfig';

const todayKey = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

const emptyForm = () => ({
  empId: '',
  name: '',
  phoneNumber: '',
  email: '',
  address: '',
  aadhaarNumber: '',
  bankName: '',
  accountNumber: '',
  dateOfJoining: todayKey(),
  photoUrl: '',
  notes: '',
  isActive: true,
  loginEnabled: true,
  permissions: { ...DEFAULT_EMPLOYEE_PERMISSIONS },
});

function PermissionEditor({ permissions, onChange }) {
  const p = normalizeEmployeePermissions(permissions);
  const setTab = (key, value) =>
    onChange({
      ...p,
      tabs: { ...p.tabs, [key]: value },
    });
  const setAction = (key, value) =>
    onChange({
      ...p,
      actions: { ...p.actions, [key]: value },
    });

  return (
    <div className="rounded-lg border border-stone-200 p-4 space-y-4 bg-stone-50/80">
      <p className="text-sm font-semibold text-stone-800">Portal permissions</p>
      <div>
        <p className="text-xs font-medium text-stone-600 mb-2">Tabs they can see</p>
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={p.tabs.dashboard}
              onChange={(e) => setTab('dashboard', e.target.checked)}
            />
            Dashboard
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={p.tabs.restroLiteShops}
              onChange={(e) => setTab('restroLiteShops', e.target.checked)}
            />
            PocketBillingAI shops
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={p.tabs.restroLiteLeads}
              onChange={(e) => setTab('restroLiteLeads', e.target.checked)}
            />
            PocketBillingAI trial leads
          </label>
        </div>
      </div>
      <div>
        <p className="text-xs font-medium text-stone-600 mb-2">Actions</p>
        <div className="grid sm:grid-cols-2 gap-2 text-sm">
          {[
            ['addShop', 'Add shop'],
            ['editShop', 'Edit shop'],
            ['extendSubscription', 'Extend subscription'],
            ['forceLogoutShop', 'Force logout shop'],
            ['deleteShop', 'Delete shop'],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={p.actions[key]}
                onChange={(e) => setAction(key, e.target.checked)}
              />
              {label}
            </label>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-medium text-stone-600 mb-2">Shop onboarding fields</p>
        <p className="text-xs text-stone-500 mb-2">
          Control which fields employees see when adding or editing PocketBillingAI shops.
        </p>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={p.actions.editPaymentLink}
            onChange={(e) => setAction('editPaymentLink', e.target.checked)}
          />
          Payment link (renewal / subscription)
        </label>
      </div>
    </div>
  );
}

function resizePhotoFile(file, maxWidth = 480) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });
}

export default function EmployeesManagementPage() {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [photoPreview, setPhotoPreview] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const active =
        activeFilter === 'active' ? 'true' : activeFilter === 'inactive' ? 'false' : undefined;
      const res = await axios.get(API_ENDPOINTS.COMPANY_EMPLOYEES(search, active));
      setEmployees(res.data.employees || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load employees');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [search, activeFilter]);

  useEffect(() => {
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
  }, [load]);

  const filtered = useMemo(() => employees, [employees]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setPhotoPreview('');
    setShowForm(true);
  };

  const openEdit = async (row) => {
    setBusy(true);
    try {
      const res = await axios.get(API_ENDPOINTS.COMPANY_EMPLOYEE(row.id));
      const e = res.data.employee;
      setEditingId(e.id);
      setForm({
        empId: e.empId || '',
        name: e.name || '',
        phoneNumber: e.phoneNumber || '',
        email: e.email || '',
        address: e.address || '',
        aadhaarNumber: e.aadhaarNumber || '',
        bankName: e.bankName || '',
        accountNumber: e.accountNumber || '',
        dateOfJoining: e.dateOfJoining || todayKey(),
        photoUrl: e.photoUrl || '',
        notes: e.notes || '',
        isActive: e.isActive !== false,
        loginEnabled: e.loginEnabled !== false,
        permissions: normalizeEmployeePermissions(e.permissions),
      });
      setPhotoPreview(e.photoUrl || '');
      setShowForm(true);
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handlePhotoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await resizePhotoFile(file);
      setForm((f) => ({ ...f, photoUrl: dataUrl }));
      setPhotoPreview(dataUrl);
    } catch (err) {
      alert(err.message);
    }
    e.target.value = '';
  };

  const saveEmployee = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        await axios.put(API_ENDPOINTS.COMPANY_EMPLOYEE(editingId), form);
      } else {
        await axios.post(API_ENDPOINTS.COMPANY_EMPLOYEES_CREATE, form);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const resendCredentials = async () => {
    if (!editingId) return;
    if (!window.confirm('Send a new one-time password to this employee by email?')) return;
    setBusy(true);
    try {
      const res = await axios.post(API_ENDPOINTS.COMPANY_EMPLOYEE_RESEND_CREDENTIALS(editingId));
      alert(res.data.message || 'Credentials sent');
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const removeEmployee = async (row) => {
    if (!window.confirm(`Remove employee "${row.name}" (${row.empId})?`)) return;
    try {
      await axios.delete(API_ENDPOINTS.COMPANY_EMPLOYEE(row.id));
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">
              RestroAI Team
            </p>
            <h1 className="text-2xl font-bold text-stone-900">Employee management</h1>
            <p className="text-sm text-stone-500 mt-1">
              Onboard company employees with ID, contact, Aadhaar, bank details, and photo.
            </p>
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
          >
            + Add employee
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="search"
            placeholder="Search by ID, name, phone, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 border border-stone-200 rounded-lg px-3 py-2 text-sm"
          />
          <select
            className="border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white"
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
          >
            <option value="all">All employees</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        {loading ? (
          <p className="text-stone-500">Loading…</p>
        ) : (
          <div className="bg-white rounded-xl border border-stone-200 overflow-x-auto shadow-sm">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-stone-50 text-stone-600">
                <tr>
                  <th className="text-left px-4 py-3">Photo</th>
                  <th className="text-left px-4 py-3">Emp ID</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Phone</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Login</th>
                  <th className="text-left px-4 py-3">Joined</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-stone-500">
                      No employees yet. Add your first team member.
                    </td>
                  </tr>
                ) : (
                  filtered.map((row) => (
                    <tr key={row.id} className="hover:bg-stone-50/80">
                      <td className="px-4 py-3">
                        {row.photoUrl ? (
                          <img
                            src={row.photoUrl}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover border border-stone-200"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-400 text-xs">
                            —
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono font-medium">{row.empId}</td>
                      <td className="px-4 py-3 font-medium">{row.name}</td>
                      <td className="px-4 py-3 tabular-nums">{row.phoneNumber}</td>
                      <td className="px-4 py-3 text-stone-600">{row.email || '—'}</td>
                      <td className="px-4 py-3 text-xs">
                        {row.loginEnabled === false ? (
                          <span className="text-stone-500">Off</span>
                        ) : row.hasPortalLogin ? (
                          <span className="text-emerald-700 font-medium">Active</span>
                        ) : (
                          <span className="text-amber-700">Pending setup</span>
                        )}
                      </td>
                      <td className="px-4 py-3 tabular-nums">{row.dateOfJoining || '—'}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                            row.isActive
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-stone-100 text-stone-600'
                          }`}
                        >
                          {row.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          className="text-indigo-600 text-xs font-medium mr-3"
                          onClick={() => openEdit(row)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="text-red-600 text-xs"
                          onClick={() => removeEmployee(row)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <form
              onSubmit={saveEmployee}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 space-y-4 max-h-[92vh] overflow-y-auto"
            >
              <h2 className="text-lg font-bold">
                {editingId ? 'Edit employee' : 'Add employee'}
              </h2>

              <div className="flex flex-col sm:flex-row gap-4 items-start">
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Employee"
                    className="w-24 h-24 rounded-lg object-cover border border-stone-200"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-lg border border-dashed border-stone-300 bg-stone-50 flex items-center justify-center text-xs text-stone-400">
                    No photo
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <label className="cursor-pointer inline-flex px-3 py-2 rounded-lg border border-stone-300 text-sm font-medium hover:bg-stone-50">
                    Upload photo
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handlePhotoPick}
                    />
                  </label>
                  {photoPreview && (
                    <button
                      type="button"
                      className="text-xs text-red-600 text-left"
                      onClick={() => {
                        setPhotoPreview('');
                        setForm((f) => ({ ...f, photoUrl: '' }));
                      }}
                    >
                      Remove photo
                    </button>
                  )}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block text-sm">
                  Employee ID *
                  <input
                    required
                    className="mt-1 w-full border rounded-lg px-3 py-2 font-mono uppercase"
                    value={form.empId}
                    onChange={(e) =>
                      setForm({ ...form, empId: e.target.value.toUpperCase() })
                    }
                    disabled={Boolean(editingId)}
                    placeholder="e.g. EMP001"
                  />
                </label>
                <label className="block text-sm">
                  Full name *
                  <input
                    required
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </label>
                <label className="block text-sm">
                  Phone number *
                  <input
                    required
                    type="tel"
                    maxLength={10}
                    className="mt-1 w-full border rounded-lg px-3 py-2 tabular-nums"
                    value={form.phoneNumber}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        phoneNumber: e.target.value.replace(/\D/g, '').slice(0, 10),
                      })
                    }
                  />
                </label>
                <label className="block text-sm">
                  Email {form.loginEnabled ? '*' : ''}
                  <input
                    type="email"
                    required={form.loginEnabled}
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Used for portal login"
                  />
                </label>
                <label className="block text-sm sm:col-span-2">
                  Address
                  <textarea
                    rows={2}
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                  />
                </label>
                <label className="block text-sm">
                  Aadhaar number
                  <input
                    inputMode="numeric"
                    maxLength={12}
                    className="mt-1 w-full border rounded-lg px-3 py-2 tabular-nums"
                    value={form.aadhaarNumber}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        aadhaarNumber: e.target.value.replace(/\D/g, '').slice(0, 12),
                      })
                    }
                    placeholder="12 digits"
                  />
                </label>
                <label className="block text-sm">
                  Date of joining
                  <input
                    type="date"
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.dateOfJoining}
                    onChange={(e) => setForm({ ...form, dateOfJoining: e.target.value })}
                  />
                </label>
                <label className="block text-sm">
                  Bank name
                  <input
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.bankName}
                    onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                  />
                </label>
                <label className="block text-sm">
                  Account number
                  <input
                    inputMode="numeric"
                    className="mt-1 w-full border rounded-lg px-3 py-2 tabular-nums"
                    value={form.accountNumber}
                    onChange={(e) => setForm({ ...form, accountNumber: e.target.value })}
                  />
                </label>
                <label className="block text-sm sm:col-span-2">
                  Notes
                  <textarea
                    rows={2}
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </label>
                <label className="flex items-center gap-2 text-sm sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                  Active employee
                </label>
                <label className="flex items-center gap-2 text-sm sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={form.loginEnabled}
                    onChange={(e) => setForm({ ...form, loginEnabled: e.target.checked })}
                  />
                  Enable portal login (onboarding email with one-time password)
                </label>
              </div>

              {form.loginEnabled && (
                <PermissionEditor
                  permissions={form.permissions}
                  onChange={(permissions) => setForm({ ...form, permissions })}
                />
              )}

              <p className="text-xs text-stone-500">
                Aadhaar and bank details are stored encrypted. Only super admins can view full
                numbers when editing. When portal login is enabled, the employee receives an email
                with a one-time password and must set a new password on first sign-in.
              </p>

              {editingId && form.loginEnabled && (
                <button
                  type="button"
                  onClick={resendCredentials}
                  disabled={busy}
                  className="text-sm text-indigo-700 font-medium hover:underline disabled:opacity-50"
                >
                  Resend login credentials (new one-time password)
                </button>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 rounded-lg border border-stone-300 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-60"
                >
                  {busy ? 'Saving…' : editingId ? 'Save changes' : 'Add employee'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </Layout>
  );
}
